import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { EXPERTISE_AREAS, expertiseLabel } from "../expertise";

const ROLE_ORDER = ["advisor", "provider", "entrepreneur", "investor", "accelerator"];
const ROLE_KEY = {
  entrepreneur: "founders",
  advisor: "advisors",
  provider: "providers",
  investor: "fundManagers",
  accelerator: "accelerators",
};
const ROLE_FILTERS = [
  { role: "", labelKey: "community.filterAllMembers" },
  { role: "entrepreneur", labelKey: "community.filterAllFounders" },
  { role: "advisor", labelKey: "community.filterAllAdvisors" },
  { role: "provider", labelKey: "community.filterAllProviders" },
  { role: "investor", labelKey: "community.filterAllInvestors" },
  { role: "accelerator", labelKey: "community.filterAllAccelerators" },
];

export default function Community() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState({});
  const [query, setQuery] = useState("");
  const [expertiseFilter, setExpertiseFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data: next } = await api.get("/community", { params: { lang } });
      setData(next);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async (other_id) => {
    setBusy((b) => ({ ...b, [other_id]: true }));
    try { await api.post("/connections", { other_id }); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, [other_id]: false })); }
  };

  const accept = async (cid) => {
    setBusy((b) => ({ ...b, [cid]: true }));
    try { await api.post(`/connections/${cid}/accept`); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy((b) => ({ ...b, [cid]: false })); }
  };

  if (!data) return <div className="tb-loading">{t("common.loading")}</div>;

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="community-heading">{t("community.title")}</h1>
      <p className="tb-muted" style={{ marginBottom: "1.5rem" }}>{t("community.subtitle")}</p>
      {err && <div className="tb-form-error" style={{ marginBottom: 16 }}>{err}</div>}

      {data.pending_in?.length > 0 && (
        <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="pending-in-section">
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>{t("community.pendingIn")}</h3>
          <div className="tb-card-grid">
            {data.pending_in.map((p) => (
              <div key={p.id} className="tb-block" data-testid={`pending-in-${p.id}`}>
                <strong>{p.other.name}</strong>
                <div className="tb-muted" style={{ fontSize: "0.78rem", margin: "4px 0 8px" }}>
                  {t(`roles.${ROLE_KEY[p.other.role] || "members"}`)} {p.other.company ? `· ${p.other.company}` : ""}
                </div>
                <button className="tb-primary-btn" onClick={() => accept(p.id)} disabled={!!busy[p.id]} data-testid={`accept-${p.id}`}>
                  {t("community.accept")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Section
        title={t("community.mineTitle")}
        subtitle={t("community.mineSubtitle")}
        groups={data.mine}
        t={t}
        testidPrefix="mine"
        pendingOut={data.pending_out}
        busy={busy}
      />

      <Section
        title={t("community.directoryTitle")}
        subtitle={t("community.directorySubtitle")}
        groups={data.directory}
        t={t}
        testidPrefix="dir"
        onConnect={connect}
        busy={busy}
        pendingOut={data.pending_out}
        currentUserId={user?.id}
        query={query}
        onQueryChange={setQuery}
        expertiseFilter={expertiseFilter}
        onExpertiseFilter={setExpertiseFilter}
        roleFilter={roleFilter}
        onRoleFilter={setRoleFilter}
        showFilters
      />
    </div>
  );
}

function matchesFilters(u, query, expertiseFilter, t) {
  if (expertiseFilter && !(u.expertise_areas || []).includes(expertiseFilter)) return false;
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    u.name,
    u.company,
    u.headline,
    u.bio,
    u.location,
    u.tagline,
    u.website_url,
    ...(u.expertise_areas || []).map((k) => expertiseLabel(t, k)),
  ].join(" ").toLowerCase();
  return hay.includes(q);
}

function Section({
  title, subtitle, groups, t, testidPrefix,
  onConnect, busy = {}, pendingOut = [], currentUserId,
  query = "", onQueryChange, expertiseFilter = "", onExpertiseFilter,
  roleFilter = "", onRoleFilter,
  showFilters = false,
}) {
  const pendingSet = new Set(pendingOut.map((p) => p.other.id || p.other_id));
  const filtered = useMemo(() => {
    const next = {};
    ROLE_ORDER.forEach((role) => {
      if (roleFilter && role !== roleFilter) {
        next[role] = [];
        return;
      }
      next[role] = (groups[role] || []).filter((u) => matchesFilters(u, query, expertiseFilter, t));
    });
    return next;
  }, [groups, query, expertiseFilter, roleFilter, t]);
  const anything = ROLE_ORDER.some((r) => (filtered[r] || []).length > 0);
  return (
    <div style={{ marginBottom: "2.5rem" }} data-testid={`section-${testidPrefix}`}>
      <h2 className="tb-h2" style={{ marginBottom: 4 }}>{title}</h2>
      <p className="tb-muted" style={{ marginTop: 0, marginBottom: "1rem", fontSize: "0.85rem" }}>{subtitle}</p>
      {showFilters && (
        <div className="tb-directory-filters" data-testid={`${testidPrefix}-filters`}>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t("community.searchPlaceholder")}
            data-testid={`${testidPrefix}-search`}
            className="tb-directory-search"
            style={{ marginBottom: 16 }}
          />
          <div className="tb-expertise-filters">
            <button
              type="button"
              className={`tb-chip ${!expertiseFilter ? "active" : ""}`}
              onClick={() => onExpertiseFilter("")}
              data-testid={`${testidPrefix}-expertise-all`}
            >
              {t("community.allExpertises")}
            </button>
            {EXPERTISE_AREAS.map((key) => (
              <button
                type="button"
                key={key}
                className={`tb-chip ${expertiseFilter === key ? "active" : ""}`}
                onClick={() => onExpertiseFilter(expertiseFilter === key ? "" : key)}
                data-testid={`${testidPrefix}-expertise-${key}`}
              >
                {expertiseLabel(t, key)}
              </button>
            ))}
          </div>
          <div className="tb-expertise-filters" style={{ marginTop: 16 }} data-testid={`${testidPrefix}-role-filters`}>
            {ROLE_FILTERS.map(({ role, labelKey }) => (
              <button
                type="button"
                key={role || "all"}
                className={`tb-chip ${roleFilter === role ? "active" : ""}`}
                onClick={() => onRoleFilter(role)}
                data-testid={`${testidPrefix}-role-${role || "all"}`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}
      {!anything && <p className="tb-muted" data-testid={`${testidPrefix}-empty`}>{t("community.empty")}</p>}
      {ROLE_ORDER.map((role) => {
        const arr = filtered[role] || [];
        if (!arr.length) return null;
        return (
          <div key={role} style={{ marginBottom: "1.5rem" }}>
            <h4 className="tb-h4" style={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.78rem", color: "var(--muted)", marginBottom: 8 }}>
              {t(`roles.${ROLE_KEY[role]}`)}
            </h4>
            <div className="tb-bio-grid">
              {arr.map((u) => (
                <MemberCard
                  key={u.id}
                  u={u}
                  t={t}
                  testid={`${testidPrefix}-card-${u.id}`}
                  onConnect={onConnect}
                  busy={busy}
                  pending={pendingSet.has(u.id)}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MemberCard({ u, t, testid, onConnect, busy = {}, pending, currentUserId }) {
  return (
    <Link
      to={`/app/u/${u.id}`}
      className="tb-bio-card"
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
      data-testid={testid}
    >
      <div className="tb-bio-card-photo">
        {u.photo_url ? (
          <img src={u.photo_url} alt="" />
        ) : (
          <span>{(u.name || "?").slice(0, 1).toUpperCase()}</span>
        )}
      </div>
      <div className="tb-bio-card-body">
        <strong>{u.name}</strong>
        {u.headline && <div className="tb-bio-headline">{u.headline}</div>}
        {u.location && <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 2 }}>{u.location}</div>}
        {!u.headline && u.company && <div className="tb-muted" style={{ fontSize: "0.78rem" }}>{u.company}</div>}
        {u.solution_slug && <span className="tb-badge good" style={{ marginTop: 10 }}>{t("solutions.viewOffering")}</span>}
        {onConnect && u.id !== currentUserId && (
          pending
            ? <span className="tb-badge warn" style={{ marginTop: 10 }}>{t("community.pendingOut")}</span>
            : <button className="tb-ghost-btn" style={{ marginTop: 10 }} onClick={(e) => { e.preventDefault(); onConnect(u.id); }} disabled={!!busy[u.id]} data-testid={`connect-${u.id}`}>
                {t("community.connect")}
              </button>
        )}
      </div>
    </Link>
  );
}
